import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Siren, Camera, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputCls = "w-full bg-[#0C0C0C] border border-white/15 rounded-md px-3 py-2.5 text-sm focus:outline-none focus:border-[#F2B705]/60";

export default function AccidentReport() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [media, setMedia] = useState([]);
  const [f, setF] = useState({
    accident_date: "", accident_time: "", location: "", statement: "",
    other_driver_name: "", other_driver_phone: "", other_driver_insurance: "", other_driver_policy: "",
    witnesses: "", police_report_number: "", injuries: "", damage_description: "",
  });
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  useEffect(() => {
    axios.get(`${API}/accident-report/${token}`).then((r) => setInfo(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "This link is invalid or has expired. Please contact AutoHire STL."));
  }, [token]);

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/accident-report/${token}/upload`, fd);
      setMedia((p) => [...p, `${process.env.REACT_APP_BACKEND_URL}${data.url}`]);
      toast.success("File added.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed — try again."); }
    finally { setBusy(false); }
  };

  const submit = async () => {
    if (!f.accident_date || !f.location.trim() || !f.statement.trim()) {
      toast.error("Accident date, location, and your statement are required."); return;
    }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/accident-report/${token}/submit`, { ...f, media_urls: media });
      toast.success(data.message);
      setDone(true);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not submit — try again."); }
    finally { setBusy(false); }
  };

  if (err) return <div className="min-h-screen bg-black text-white flex items-center justify-center p-6 text-center">{err}</div>;
  if (!info) return <div className="min-h-screen bg-black flex items-center justify-center"><Loader2 className="animate-spin text-[#F2B705]" size={28} /></div>;
  if (done) return (
    <div className="min-h-screen bg-black text-white flex flex-col items-center justify-center p-6 text-center gap-3">
      <CheckCircle2 className="text-emerald-400" size={44} />
      <h1 className="font-display font-bold text-2xl">Report Received</h1>
      <p className="text-white/60 text-sm max-w-md">Thank you{info.name ? `, ${info.name}` : ""}. AutoHire has been notified and will follow up with you shortly. If anyone is injured or in danger, call 911.</p>
    </div>
  );

  return (
    <div className="min-h-screen bg-black text-white p-4 pb-16">
      <div className="max-w-lg mx-auto">
        <div className="flex items-center gap-2 text-[#F2B705] text-xs font-bold uppercase tracking-widest mt-4"><Siren size={15} /> Accident / Incident Report</div>
        <h1 className="font-display font-black text-2xl mt-1.5">Hi {info.name} — tell us what happened</h1>
        <p className="text-white/50 text-sm mt-1">Rental: <b className="text-white">{info.vehicle}</b>. If anyone is injured or in danger, call 911 first.</p>

        <div className="mt-5 space-y-3" data-testid="accident-form">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-white/45">Accident Date *</label>
              <input data-testid="acc-date" type="date" className={`${inputCls} [color-scheme:dark]`} value={f.accident_date} onChange={(e) => set("accident_date", e.target.value)} />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-white/45">Approx. Time</label>
              <input data-testid="acc-time" type="time" className={`${inputCls} [color-scheme:dark]`} value={f.accident_time} onChange={(e) => set("accident_time", e.target.value)} />
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-white/45">Location *</label>
            <input data-testid="acc-location" className={inputCls} placeholder="Street / intersection, city" value={f.location} onChange={(e) => set("location", e.target.value)} />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-white/45">What happened? (your statement) *</label>
            <textarea data-testid="acc-statement" rows={4} className={inputCls} placeholder="Describe the accident or incident in your own words" value={f.statement} onChange={(e) => set("statement", e.target.value)} />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-white/45">Injuries (if any)</label>
            <input data-testid="acc-injuries" className={inputCls} placeholder="e.g., none, or describe" value={f.injuries} onChange={(e) => set("injuries", e.target.value)} />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-white/45">Damage to the vehicle</label>
            <input data-testid="acc-damage" className={inputCls} placeholder="e.g., front bumper, driver door" value={f.damage_description} onChange={(e) => set("damage_description", e.target.value)} />
          </div>

          <div className="rounded-lg border border-white/12 p-3.5 space-y-2.5">
            <p className="text-xs font-bold text-white/70 uppercase tracking-wide">Other driver (if involved)</p>
            <input data-testid="acc-od-name" className={inputCls} placeholder="Other driver's name" value={f.other_driver_name} onChange={(e) => set("other_driver_name", e.target.value)} />
            <input data-testid="acc-od-phone" className={inputCls} placeholder="Other driver's phone" value={f.other_driver_phone} onChange={(e) => set("other_driver_phone", e.target.value)} />
            <input data-testid="acc-od-ins" className={inputCls} placeholder="Their insurance company (if available)" value={f.other_driver_insurance} onChange={(e) => set("other_driver_insurance", e.target.value)} />
            <input data-testid="acc-od-policy" className={inputCls} placeholder="Their policy number (if available)" value={f.other_driver_policy} onChange={(e) => set("other_driver_policy", e.target.value)} />
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-widest text-white/45">Police report number (if any)</label>
            <input data-testid="acc-police" className={inputCls} placeholder="Report # or department" value={f.police_report_number} onChange={(e) => set("police_report_number", e.target.value)} />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-white/45">Witnesses (names / phones)</label>
            <input data-testid="acc-witnesses" className={inputCls} placeholder="If available" value={f.witnesses} onChange={(e) => set("witnesses", e.target.value)} />
          </div>

          <div className="rounded-lg border border-white/12 p-3.5">
            <p className="text-xs font-bold text-white/70 uppercase tracking-wide flex items-center gap-1.5"><Camera size={13} /> Photos / videos ({media.length} added)</p>
            <input data-testid="acc-file" type="file" accept="image/*,video/*,application/pdf" className="mt-2 text-xs text-white/60 file:mr-3 file:rounded-md file:border-0 file:bg-[#F2B705] file:px-3 file:py-1.5 file:text-xs file:font-bold file:text-black"
              onChange={(e) => upload(e.target.files?.[0])} />
            {media.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {media.map((m, i) => <a key={i} href={m} target="_blank" rel="noreferrer" className="text-[10px] text-[#F2B705] underline">file {i + 1}</a>)}
              </div>
            )}
          </div>

          <button data-testid="acc-submit" onClick={submit} disabled={busy}
            className="w-full bg-[#F2B705] text-black font-bold py-3.5 rounded-md hover:bg-white transition-colors flex items-center justify-center gap-2 disabled:opacity-50">
            {busy && <Loader2 className="animate-spin" size={16} />} Submit Report to AutoHire
          </button>
          <p className="text-white/35 text-[10px] text-center">Your report is tied to your rental record and reviewed by AutoHire management.</p>
        </div>
      </div>
    </div>
  );
}
